import {
  HIDDEN_COACH_DASHBOARD_SUBTAB_REDIRECT,
  SHOW_COACH_NUTRITION_KPI_DASHBOARD,
  SHOW_COACH_PROGRAMS_KPI_DASHBOARD,
} from './coachUiVisibility';

/**
 * Route Slugs — Single Source of Truth
 *
 * Owns all slug <-> tab mapping, path/query normalization,
 * context ownership, special-route gating, and title mapping.
 *
 * Rules:
 * - resolvePath NEVER throws
 * - Paths are lowercase, no trailing slash (except "/")
 * - Query params are whitelisted, validated, and sorted
 * - Context params only appear on tabs that own them
 */

// ---------------------------------------------------------------------------
// 1. Canonical slug <-> tab maps
// ---------------------------------------------------------------------------

const TAB_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  clients: '/clients',
  chat: '/chat',
  calendar: '/calendar',
  'add-client': '/add-client',
  programs: '/programs',
  nutrition: '/nutrition',
  supplements: '/supplements',
  'forms-trackers': '/forms',
  ai: '/ai-tools',
  workouts: '/workouts',
  // Client Check-Ins hub. Tab id is historically 'goals'; '/goals' stays as an
  // alias below so old links keep working.
  goals: '/check-ins',
  videos: '/videos',
  progress: '/progress',
  messages: '/messages',
  payments: '/payments',
  wearables: '/wearables',
  automations: '/automations',
  'client-billing': '/client-billing',
  settings: '/settings',
  /** Unified upgrade page — not in sidebar; see `CoachUpgradeHub`. */
  upgrade: '/upgrade',
};

const PATH_TO_TAB: Record<string, string> = {};
for (const [tab, path] of Object.entries(TAB_TO_PATH)) {
  PATH_TO_TAB[path] = tab;
}

const ALIASES: Record<string, string> = {
  '/ai': '/ai-tools',
  '/forms-trackers': '/forms',
  '/health': '/wearables',
  '/goals': '/check-ins',
};

// ---------------------------------------------------------------------------
// 1b. Sub-tab slug maps (two-segment paths)
// ---------------------------------------------------------------------------

const SUBTAB_MAP: Record<string, { tab: string; subTab: string }> = {
  '/clients/all': { tab: 'clients', subTab: 'clients-all' },
  '/clients/connected': { tab: 'clients', subTab: 'clients-connected' },
  '/clients/pending': { tab: 'clients', subTab: 'clients-pending' },
  '/clients/archived': { tab: 'clients', subTab: 'clients-archived' },
  '/programs/dashboard': { tab: 'programs', subTab: 'programs-dashboard' },
  '/programs/all': { tab: 'programs', subTab: 'programs-all' },
  '/programs/workouts': { tab: 'programs', subTab: 'programs-workouts' },
  '/programs/exercises': { tab: 'programs', subTab: 'programs-exercises' },
  '/nutrition/dashboard': { tab: 'nutrition', subTab: 'nutrition-dashboard' },
  '/nutrition/meal-plans': { tab: 'nutrition', subTab: 'nutrition-meal-plans' },
  '/nutrition/clients': { tab: 'nutrition', subTab: 'nutrition-clients' },
  '/nutrition/meals': { tab: 'nutrition', subTab: 'nutrition-meals' },
  '/forms/inbox': { tab: 'forms-trackers', subTab: 'forms-trackers-inbox' },
  '/forms/forms': { tab: 'forms-trackers', subTab: 'forms-trackers-forms' },
  '/forms/trackers': { tab: 'forms-trackers', subTab: 'forms-trackers-trackers' },
  '/ai-tools/patterns': { tab: 'ai', subTab: 'ai-patterns' },
  '/ai-tools/chat': { tab: 'ai', subTab: 'ai-chat' },
};

const SUBTAB_TO_PATH: Record<string, string> = {};
for (const [path, { subTab }] of Object.entries(SUBTAB_MAP)) {
  SUBTAB_TO_PATH[subTab] = path;
}

// ---------------------------------------------------------------------------
// 2. Special routes (auth/onboarding) — bypass slug logic entirely
// ---------------------------------------------------------------------------

export const SPECIAL_ROUTE_PREFIXES: string[] = [
  '/signup',
  '/coach/auth',
  '/coach/sign-in',
  '/coach/sign-up',
  '/coach/signup',
  '/coach/onboarding',
  '/client/auth',
  '/client/signin',
  '/join',
  '/intake/review',
  '/onboarding',
  '/auth/callback',
  '/auth/sign-in',
  '/checkout',
  '/reset-password',
];

export function isSpecialRoute(pathname: string): boolean {
  const lower = pathname.toLowerCase();
  return SPECIAL_ROUTE_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

// ---------------------------------------------------------------------------
// 3. Context ownership — which tabs own which query params
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALLOWED_VIEWS = new Set(['overview', 'profile', 'programs', 'editor', 'nutrition']);
/** Inner tabs of the client profile (EnhancedClientProfile) — deep-linkable via ?tab=. */
export const PROFILE_TABS = new Set([
  'overview',
  'workout',
  'nutrition',
  'supplements',
  'forms',
  'health',
  'intake',
  'notes',
]);

interface RouteContext {
  clientId?: string;
  view?: string;
  /** Client-profile inner tab (PROFILE_TABS). */
  tab?: string;
  insightId?: string;
  billing?: string;
  sessionId?: string;
}

const CONTEXT_OWNERS: Record<string, Set<string>> = {
  clientId: new Set(['clients', 'dashboard']),
  view: new Set(['clients']),
  tab: new Set(['clients']),
  insightId: new Set(['clients', 'ai']),
  billing: new Set(['settings']),
  sessionId: new Set(['settings']),
};

function ownedContext(tab: string, raw: RouteContext): RouteContext {
  const out: RouteContext = {};
  if (raw.clientId && CONTEXT_OWNERS.clientId.has(tab)) out.clientId = raw.clientId;
  if (raw.view && CONTEXT_OWNERS.view.has(tab)) out.view = raw.view;
  if (raw.tab && CONTEXT_OWNERS.tab.has(tab)) out.tab = raw.tab;
  if (raw.insightId && CONTEXT_OWNERS.insightId.has(tab)) out.insightId = raw.insightId;
  if (raw.billing && CONTEXT_OWNERS.billing.has(tab)) out.billing = raw.billing;
  if (raw.sessionId && CONTEXT_OWNERS.sessionId.has(tab)) out.sessionId = raw.sessionId;
  return out;
}

// ---------------------------------------------------------------------------
// 4. Query normalization helpers
// ---------------------------------------------------------------------------

function validateUuid(v: string | null): string | undefined {
  return v && UUID_RE.test(v) ? v : undefined;
}

function validateView(v: string | null): string | undefined {
  return v && ALLOWED_VIEWS.has(v) ? v : undefined;
}

const ALLOWED_BILLING = new Set(['success', 'canceled', 'upgrade']);

function parseContext(search: string): RouteContext {
  try {
    const sp = new URLSearchParams(search);
    const billingVal = sp.get('billing');
    const tabVal = sp.get('tab');
    return {
      clientId: validateUuid(sp.get('clientId')),
      view: validateView(sp.get('view')),
      tab: tabVal && PROFILE_TABS.has(tabVal) ? tabVal : undefined,
      insightId: validateUuid(sp.get('insightId')),
      billing: billingVal && ALLOWED_BILLING.has(billingVal) ? billingVal : undefined,
      sessionId: sp.get('session_id') || undefined,
    };
  } catch {
    return {};
  }
}

function buildQuery(ctx: RouteContext): string {
  const entries: [string, string][] = [];
  if (ctx.billing) entries.push(['billing', ctx.billing]);
  if (ctx.clientId) entries.push(['clientId', ctx.clientId]);
  if (ctx.insightId) entries.push(['insightId', ctx.insightId]);
  if (ctx.sessionId) entries.push(['session_id', ctx.sessionId]);
  if (ctx.tab) entries.push(['tab', ctx.tab]);
  if (ctx.view) entries.push(['view', ctx.view]);
  // Sorted alphabetically (b, c, i, s, t, v)
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
}

// ---------------------------------------------------------------------------
// 5. Path normalization
// ---------------------------------------------------------------------------

function normalizePath(raw: string): string {
  let p = raw.toLowerCase();
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

// ---------------------------------------------------------------------------
// 6. resolvePath — NEVER throws
// ---------------------------------------------------------------------------

export interface ResolvedRoute {
  tab: string;
  subTab: string;
  context: RouteContext;
  canonicalPath: string;
  shouldReplace: boolean;
}

export function resolvePath(pathname: string, search: string = ''): ResolvedRoute {
  const fallback: ResolvedRoute = {
    tab: 'dashboard',
    subTab: '',
    context: {},
    canonicalPath: '/dashboard',
    shouldReplace: true,
  };

  try {
    const normalized = normalizePath(pathname);
    let shouldReplace = normalized !== pathname;

    // Root -> dashboard
    if (normalized === '/' || normalized === '') {
      return { ...fallback, shouldReplace: true };
    }

    // Resolve aliases (top-level only)
    let resolved = normalized;
    if (ALIASES[normalized]) {
      resolved = ALIASES[normalized];
      shouldReplace = true;
    }

    // 1) Check sub-tab map first (two-segment paths like /programs/workouts)
    const subMatch = SUBTAB_MAP[resolved];
    if (subMatch) {
      const rawCtx = parseContext(search);
      const { tab } = subMatch;
      let { subTab } = subMatch;
      const redirectTarget = HIDDEN_COACH_DASHBOARD_SUBTAB_REDIRECT[subTab];
      if (redirectTarget) {
        const programsHidden = subTab === 'programs-dashboard' && !SHOW_COACH_PROGRAMS_KPI_DASHBOARD;
        const nutritionHidden = subTab === 'nutrition-dashboard' && !SHOW_COACH_NUTRITION_KPI_DASHBOARD;
        if (programsHidden || nutritionHidden) {
          subTab = redirectTarget;
          shouldReplace = true;
        }
      }
      const ctx = ownedContext(tab, rawCtx);
      const originalQuery = search ? '?' + search.replace(/^\?/, '') : '';
      const normalizedQuery = buildQuery(ctx);
      if (originalQuery !== normalizedQuery) shouldReplace = true;
      if (resolved !== normalized) shouldReplace = true;

      const canonicalBase = SUBTAB_TO_PATH[subTab] || resolved;

      return {
        tab,
        subTab,
        context: ctx,
        canonicalPath: canonicalBase + normalizedQuery,
        shouldReplace,
      };
    }

    // 2) Check top-level tab map
    const tab = PATH_TO_TAB[resolved];
    if (tab) {
      const rawCtx = parseContext(search);
      const ctx = ownedContext(tab, rawCtx);
      const originalQuery = search ? '?' + search.replace(/^\?/, '') : '';
      const normalizedQuery = buildQuery(ctx);
      if (originalQuery !== normalizedQuery) shouldReplace = true;
      if (resolved !== normalized) shouldReplace = true;

      return {
        tab,
        subTab: '',
        context: ctx,
        canonicalPath: resolved + normalizedQuery,
        shouldReplace,
      };
    }

    // 3) Unknown sub-path under a known parent (e.g. /programs/nonexistent -> /programs)
    const firstSegment = resolved.split('/').slice(0, 2).join('/');
    const parentTab = PATH_TO_TAB[firstSegment];
    if (parentTab) {
      return {
        tab: parentTab,
        subTab: '',
        context: {},
        canonicalPath: firstSegment,
        shouldReplace: true,
      };
    }

    return { ...fallback, shouldReplace: true };
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// 7. buildPath — tab + optional context -> canonical URL
// ---------------------------------------------------------------------------

export function buildPath(tab: string, context?: RouteContext, subTab?: string): string {
  let effectiveSubTab = subTab;
  if (effectiveSubTab) {
    const redirectTarget = HIDDEN_COACH_DASHBOARD_SUBTAB_REDIRECT[effectiveSubTab];
    if (redirectTarget) {
      const programsHidden = effectiveSubTab === 'programs-dashboard' && !SHOW_COACH_PROGRAMS_KPI_DASHBOARD;
      const nutritionHidden = effectiveSubTab === 'nutrition-dashboard' && !SHOW_COACH_NUTRITION_KPI_DASHBOARD;
      if (programsHidden || nutritionHidden) {
        effectiveSubTab = redirectTarget;
      }
    }
  }
  const basePath = (effectiveSubTab && SUBTAB_TO_PATH[effectiveSubTab]) || TAB_TO_PATH[tab] || '/dashboard';
  const ctx = context ? ownedContext(tab, context) : {};
  return basePath + buildQuery(ctx);
}

// ---------------------------------------------------------------------------
// 8. Title map
// ---------------------------------------------------------------------------

const TAB_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  clients: 'Clients',
  chat: 'Chat',
  'add-client': 'Add Client',
  programs: 'Programs',
  nutrition: 'Nutrition',
  supplements: 'Supplements',
  'forms-trackers': 'Forms & Trackers',
  ai: 'AI Tools',
  workouts: 'Workouts',
  goals: 'Check-Ins',
  videos: 'Videos',
  progress: 'Progress',
  messages: 'Messages',
  payments: 'Payments',
  wearables: 'Health',
  automations: 'Automations',
  'client-billing': 'Client Billing',
  settings: 'Settings',
  upgrade: 'Upgrade',
};

const SUBTAB_TITLES: Record<string, string> = {
  'clients-all': 'All Clients',
  'clients-connected': 'Connected',
  'clients-pending': 'Pending',
  'clients-archived': 'Archived',
  'programs-dashboard': 'Dashboard',
  'programs-all': 'All Programs',
  'programs-workouts': 'Workouts',
  'programs-exercises': 'Exercises',
  'nutrition-dashboard': 'Dashboard',
  'nutrition-meal-plans': 'Meal Plans',
  'nutrition-clients': 'Clients',
  'nutrition-meals': 'Meals',
  'forms-trackers-forms': 'Forms',
  'forms-trackers-trackers': 'Trackers',
  'ai-patterns': 'Pattern Detection',
  'ai-chat': 'Chat Analysis',
};

const APP_NAME = "Coach'd";

export function getDocumentTitle(tab: string, clientName?: string, subTab?: string): string {
  const base = TAB_TITLES[tab] || 'Dashboard';
  const sub = subTab ? SUBTAB_TITLES[subTab] : undefined;
  const title = sub ? `${sub} | ${base}` : base;
  if (clientName) return `${clientName} \u2022 ${title} | ${APP_NAME}`;
  return `${title} | ${APP_NAME}`;
}

// ---------------------------------------------------------------------------
// 9. History helpers — avoid duplicate entries + guard loops
// ---------------------------------------------------------------------------

export function currentCanonicalUrl(): string {
  try {
    const loc = window.location;
    const resolved = resolvePath(loc.pathname, loc.search);
    return resolved.canonicalPath;
  } catch {
    return '/dashboard';
  }
}

export function pushRoute(tab: string, context?: RouteContext, subTab?: string): void {
  const next = buildPath(tab, context, subTab);
  const current = currentCanonicalUrl();
  if (next === current) return;
  window.history.pushState({ tab, subTab: subTab || '', context: context || {} }, '', next);
  document.title = getDocumentTitle(tab, undefined, subTab);
}

export function replaceRoute(tab: string, context?: RouteContext, subTab?: string): void {
  const next = buildPath(tab, context, subTab);
  window.history.replaceState({ tab, subTab: subTab || '', context: context || {} }, '', next);
  document.title = getDocumentTitle(tab, undefined, subTab);
}
